import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS, PRICE_BASES, RENTAL_PERIODS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { api, errorText } from '../../api/client';
import {
  type Location,
  PERIOD_LABEL,
  type PriceComparison,
  type PriceSheet,
  type RentalPrice,
  type Supplier,
  type SupplierPrice,
  vnd,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

const BASIS_LABEL = { per_period: 'Trọn kỳ', per_day: 'Đơn giá/ngày' } as const;

interface PriceForm {
  rental_period: (typeof RENTAL_PERIODS)[number];
  price_basis: (typeof PRICE_BASES)[number];
  amount: number;
  location_id?: string | null;
  supplier_id?: string;
  min_period_count?: number;
  is_public?: boolean;
  valid?: [Dayjs | null, Dayjs | null] | null;
}

const byPeriod = (a: { rental_period: string }, b: { rental_period: string }) =>
  RENTAL_PERIODS.indexOf(a.rental_period as never) -
  RENTAL_PERIODS.indexOf(b.rental_period as never);

function marginTag(m: number | null | undefined) {
  if (m === null || m === undefined) return <Typography.Text type="secondary">—</Typography.Text>;
  const color = m < 10 ? 'red' : m < 20 ? 'orange' : 'green';
  return <Tag color={color}>{m.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%</Tag>;
}

/**
 * Giá thuê của sản phẩm:
 *  - Bảng so sánh giá bán ↔ giá nhập rẻ nhất ↔ % lãi gộp (so theo đơn giá/ngày; 7 ngày/tuần, 30 ngày/tháng – D-08)
 *  - Giá bán (PRD 6.4)  - Giá nhập theo đối tác (CR-19, chỉ quyền cost.*)
 */
export function PricesTab({ productId, onChanged }: { productId: string; onChanged: () => void }) {
  const { can } = useAuth();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canSell = can(PERMISSIONS.PRICES_MANAGE);
  const canCostRead = can(PERMISSIONS.COST_READ);
  const canCost = can(PERMISSIONS.COST_MANAGE);
  const [editing, setEditing] = useState<{
    kind: 'sell' | 'cost';
    row?: RentalPrice | SupplierPrice;
  } | null>(null);
  const [form] = Form.useForm<PriceForm>();

  const sheet = useQuery({
    queryKey: ['price-sheet', productId],
    queryFn: () => api.get<PriceSheet>(`/admin/products/${productId}/prices`),
  });
  const locs = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/admin/locations'),
  });
  const sups = useQuery({
    queryKey: ['suppliers-all'],
    queryFn: async () =>
      (await api.page<Supplier>('/admin/suppliers', { limit: 100, status: 'active' })).items,
    enabled: canCostRead,
  });

  const reload = () => {
    void qc.invalidateQueries({ queryKey: ['price-sheet', productId] });
    onChanged();
  };

  const open = (kind: 'sell' | 'cost', row?: RentalPrice | SupplierPrice) => {
    setEditing({ kind, row });
    form.resetFields();
    if (row) {
      form.setFieldsValue({
        rental_period: row.rental_period,
        price_basis: row.price_basis,
        amount: 'price' in row ? row.price : row.cost_price,
        location_id: row.location_id,
        min_period_count: (row as RentalPrice).min_period_count,
        is_public: (row as RentalPrice).is_public,
        supplier_id: (row as SupplierPrice).supplier_id,
        valid: [
          row.valid_from ? dayjs(row.valid_from) : null,
          row.valid_to ? dayjs(row.valid_to) : null,
        ],
      });
    } else {
      form.setFieldsValue({
        rental_period: 'day',
        price_basis: 'per_period',
        is_public: true,
        min_period_count: 1,
      });
    }
  };

  const submit = async () => {
    const v = await form.validateFields();
    const valid_from = v.valid?.[0]?.startOf('day').toISOString() ?? null;
    const valid_to = v.valid?.[1]?.endOf('day').toISOString() ?? null;
    try {
      if (editing?.kind === 'sell') {
        const body = {
          price_basis: v.price_basis,
          price: v.amount,
          min_period_count: v.min_period_count ?? 1,
          is_public: v.is_public ?? true,
          valid_from,
          valid_to,
        };
        if (editing.row) await api.patch(`/admin/rental-prices/${editing.row.id}`, body);
        else
          await api.post('/admin/rental-prices', {
            ...body,
            product_id: productId,
            rental_period: v.rental_period,
            location_id: v.location_id ?? null,
          });
      } else if (editing?.kind === 'cost') {
        const body = { price_basis: v.price_basis, cost_price: v.amount, valid_from, valid_to };
        if (editing.row) await api.patch(`/admin/supplier-prices/${editing.row.id}`, body);
        else
          await api.post('/admin/supplier-prices', {
            ...body,
            product_id: productId,
            supplier_id: v.supplier_id,
            rental_period: v.rental_period,
            location_id: v.location_id ?? null,
          });
      }
      message.success('Đã lưu giá');
      setEditing(null);
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  };

  const toggle = async (kind: 'sell' | 'cost', row: RentalPrice | SupplierPrice) => {
    try {
      const path = kind === 'sell' ? 'rental-prices' : 'supplier-prices';
      await api.patch(`/admin/${path}/${row.id}`, {
        status: row.status === 'active' ? 'inactive' : 'active',
      });
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  };

  const d = sheet.data;
  const validText = (r: { valid_from: string | null; valid_to: string | null }) =>
    r.valid_from || r.valid_to
      ? `${r.valid_from ? dayjs(r.valid_from).format('DD/MM/YYYY') : '…'} → ${r.valid_to ? dayjs(r.valid_to).format('DD/MM/YYYY') : '…'}`
      : 'Không thời hạn';
  const locOptions = (locs.data ?? []).map((l) => ({ value: l.id, label: l.name }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
        type="info"
        showIcon
        message={`Giá chưa gồm VAT. So sánh lãi gộp quy về đơn giá/ngày: 1 tuần = ${d?.period_days.days_per_week ?? 7} ngày, 1 tháng = ${d?.period_days.days_per_month ?? 30} ngày. Giá đặt cho tỉnh áp dụng cho vùng tỉnh cũ bên trong.`}
      />

      <Card size="small" title="Tổng hợp giá đang hiệu lực">
        <Table<PriceComparison>
          size="small"
          pagination={false}
          rowKey={(r) => `${r.rental_period}-${r.location.id ?? 'default'}`}
          dataSource={d?.comparison}
          loading={sheet.isLoading}
          locale={{ emptyText: 'Chưa có giá' }}
          columns={[
            { title: 'Kỳ thuê', render: (_, r) => PERIOD_LABEL[r.rental_period] },
            { title: 'Khu vực', render: (_, r) => r.location.name },
            {
              title: 'Giá bán',
              render: (_, r) =>
                r.sell ? (
                  <Space size={4}>
                    {r.sell.display}
                    {!r.sell.is_public && <Tag>Nội bộ</Tag>}
                    {r.sell.is_demo && <Tag color="purple">DEMO</Tag>}
                  </Space>
                ) : (
                  '—'
                ),
            },
            ...(canCostRead
              ? [
                  {
                    title: 'Giá nhập rẻ nhất',
                    render: (_: unknown, r: PriceComparison) =>
                      r.best_cost ? `${r.best_cost.display} · ${r.best_cost.supplier.name}` : '—',
                  },
                  {
                    title: 'Lãi gộp',
                    render: (_: unknown, r: PriceComparison) => marginTag(r.gross_margin_percent),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Card
        size="small"
        title="Giá bán"
        extra={
          canSell && (
            <Button icon={<PlusOutlined />} onClick={() => open('sell')}>
              Thêm giá bán
            </Button>
          )
        }
      >
        <Table<RentalPrice>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={[...(d?.rental_prices ?? [])].sort(byPeriod)}
          columns={[
            { title: 'Kỳ', render: (_, r) => PERIOD_LABEL[r.rental_period] },
            { title: 'Khu vực', render: (_, r) => r.location?.name ?? 'Mặc định' },
            { title: 'Cách tính', render: (_, r) => BASIS_LABEL[r.price_basis] },
            { title: 'Giá', align: 'right', render: (_, r) => vnd(r.price) },
            { title: 'Hiệu lực', render: (_, r) => validText(r) },
            {
              title: 'Trạng thái',
              render: (_, r) => (
                <Space size={4}>
                  <Tag color={r.status === 'active' ? 'green' : 'default'}>
                    {r.status === 'active' ? 'Đang dùng' : 'Ngừng'}
                  </Tag>
                  {!r.is_public && <Tag>Nội bộ</Tag>}
                  {r.is_demo && <Tag color="purple">DEMO</Tag>}
                </Space>
              ),
            },
            ...(canSell
              ? [
                  {
                    title: '',
                    render: (_: unknown, r: RentalPrice) => (
                      <Space>
                        <Button size="small" onClick={() => open('sell', r)}>
                          Sửa
                        </Button>
                        <Button size="small" onClick={() => void toggle('sell', r)}>
                          {r.status === 'active' ? 'Ngừng' : 'Dùng lại'}
                        </Button>
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      {canCostRead && (
        <Card
          size="small"
          title={
            <>
              Giá nhập theo đối tác <Tag color="red">Nội bộ – khách không thấy</Tag>
            </>
          }
          extra={
            canCost && (
              <Button icon={<PlusOutlined />} onClick={() => open('cost')}>
                Thêm giá nhập
              </Button>
            )
          }
        >
          <Table<SupplierPrice>
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={[...(d?.supplier_prices ?? [])].sort(byPeriod)}
            columns={[
              { title: 'Đối tác', render: (_, r) => r.supplier.name },
              { title: 'Kỳ', render: (_, r) => PERIOD_LABEL[r.rental_period] },
              { title: 'Khu vực', render: (_, r) => r.location?.name ?? 'Mặc định' },
              { title: 'Cách tính', render: (_, r) => BASIS_LABEL[r.price_basis] },
              { title: 'Giá nhập', align: 'right', render: (_, r) => vnd(r.cost_price) },
              { title: 'Hiệu lực', render: (_, r) => validText(r) },
              { title: 'Ghi chú', dataIndex: 'note' },
              {
                title: 'Trạng thái',
                render: (_, r) => (
                  <Tag color={r.status === 'active' ? 'green' : 'default'}>
                    {r.status === 'active' ? 'Đang dùng' : 'Ngừng'}
                  </Tag>
                ),
              },
              ...(canCost
                ? [
                    {
                      title: '',
                      render: (_: unknown, r: SupplierPrice) => (
                        <Space>
                          <Button size="small" onClick={() => open('cost', r)}>
                            Sửa
                          </Button>
                          <Button size="small" onClick={() => void toggle('cost', r)}>
                            {r.status === 'active' ? 'Ngừng' : 'Dùng lại'}
                          </Button>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      )}

      <Modal
        open={editing !== null}
        title={
          editing?.kind === 'cost'
            ? editing.row
              ? 'Sửa giá nhập'
              : 'Thêm giá nhập'
            : editing?.row
              ? 'Sửa giá bán'
              : 'Thêm giá bán'
        }
        onCancel={() => setEditing(null)}
        onOk={() => void submit()}
        okText="Lưu"
        forceRender
      >
        <Form form={form} layout="vertical">
          {editing?.kind === 'cost' && (
            <Form.Item name="supplier_id" label="Đối tác" rules={[{ required: true }]}>
              <Select
                disabled={!!editing.row}
                showSearch
                optionFilterProp="label"
                options={(sups.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          )}
          <Space wrap>
            <Form.Item name="rental_period" label="Kỳ thuê" rules={[{ required: true }]}>
              <Select
                disabled={!!editing?.row}
                style={{ width: 140 }}
                options={RENTAL_PERIODS.map((p) => ({ value: p, label: PERIOD_LABEL[p] }))}
              />
            </Form.Item>
            <Form.Item
              name="price_basis"
              label="Cách tính"
              rules={[{ required: true }]}
              tooltip="Trọn kỳ: 2.700.000đ/tuần. Đơn giá/ngày: 700.000đ/ngày khi thuê theo tuần."
            >
              <Select
                style={{ width: 160 }}
                options={PRICE_BASES.map((b) => ({ value: b, label: BASIS_LABEL[b] }))}
              />
            </Form.Item>
          </Space>
          <Form.Item
            name="amount"
            label={
              editing?.kind === 'cost' ? 'Giá nhập (VNĐ, chưa VAT)' : 'Giá bán (VNĐ, chưa VAT)'
            }
            rules={[{ required: true, type: 'number', min: 0 }]}
          >
            <InputNumber
              style={{ width: 240 }}
              step={50000}
              formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
              parser={(v) => Number((v ?? '').replace(/\./g, '')) as 0}
              suffix="đ"
            />
          </Form.Item>
          <Form.Item
            name="location_id"
            label="Khu vực"
            tooltip="Để mặc định nếu giá áp dụng mọi khu vực"
          >
            <Select
              disabled={!!editing?.row}
              allowClear
              placeholder="Mặc định (mọi khu vực)"
              options={locOptions}
            />
          </Form.Item>
          {editing?.kind === 'sell' && (
            <Space wrap>
              <Form.Item name="min_period_count" label="Số kỳ tối thiểu">
                <InputNumber min={1} max={365} />
              </Form.Item>
              <Form.Item name="is_public" label="Công khai cho khách" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
          )}
          <Form.Item
            name="valid"
            label="Thời gian hiệu lực"
            tooltip="Để trống = áp dụng không thời hạn. Các mức giá cùng phạm vi không được chồng thời gian."
          >
            <DatePicker.RangePicker allowEmpty={[true, true]} format="DD/MM/YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
